import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Card, DataTable, Button, Select, InlineGrid, InlineStack, Box, BlockStack, Tabs, Text, Banner } from '@shopify/polaris';
import { ExportIcon, MaximizeIcon } from '@shopify/polaris-icons';
import { saveAs } from 'file-saver';
import { formatToCurrency, formatDate, generateArrayOfYears, getRequest } from '../../shared/components/functions.jsx';
import CashbookModal from './cashbookModal.jsx';
import CashBalanceChart, { CashBalanceChartCaption, formatWallClockDate } from './CashBalanceChart.jsx';
import KpiTile from '../analytics/components/dashboard/KpiTile';
import { COLORS } from '../analytics/components/dashboard/chartTheme';
import Toasts from '../../shared/components/toasts/toasts.jsx';
import { useUrl } from '../../shared/util/hanldesave';

const TABS = [
  { id: 'entries', content: 'Entries' },
  { id: 'balance', content: 'Balance' }
];

// in cents, like the balance chain and the Excel export (pre-2002 amounts are converted francs with many decimals)
const sumOf = (items, field) => items.reduce((cents, item) => cents + Math.round((Number(item[field]) || 0) * 100), 0) / 100;
const entriesLabel = (count) => `${count} ${count === 1 ? 'entry' : 'entries'}`;

export default function CashbookPage() {
  let url = useUrl();
  const navigate = useNavigate();

  const [year, setYear] = useState(url.get('year') || String(new Date().getFullYear()));
  const [tab, setTab] = useState(
    Math.max(
      0,
      TABS.findIndex((t) => t.id === url.get('tab'))
    )
  );
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('new');
  const [toast, setToast] = useState(false);
  const [errorToast, setErrorToast] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchTransactions = async () => {
    const [transactions, balanceData] = await Promise.all([getRequest(`/api/cashbook?year=${year}`), getRequest(`/api/cashbook/balance?year=${year}`)]);
    setItems(Array.isArray(transactions) ? transactions : []);
    setBalance(balanceData?.configured === undefined ? null : balanceData);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // the selected year as an .xlsx file: opening balance, entries oldest first, totals (server/services/cashbookExport.js)
  const exportExcel = async () => {
    setExporting(true);
    try {
      const response = await fetch(`/api/cashbook/export?year=${year}`);
      if (!response.ok) throw new Error(`export failed with ${response.status}`);
      saveAs(await response.blob(), `cashbook_${year}.xlsx`);
    } catch (err) {
      console.log(err);
      setErrorToast(true);
    }
    setExporting(false);
  };

  const updateUrl = useCallback(
    (params) => {
      const search = new URLSearchParams(window.location.search);
      Object.entries(params).forEach(([key, value]) => (value ? search.set(key, value) : search.delete(key)));
      navigate({ pathname: window.location.pathname, search: `?${search}` }, { replace: true });
    },
    [navigate]
  );

  const handleYearChange = useCallback(
    (value) => {
      setYear(value);
      updateUrl({ year: value });
    },
    [updateUrl]
  );

  const handleTabChange = useCallback(
    (index) => {
      setTab(index);
      updateUrl({ tab: index === 0 ? null : TABS[index].id });
    },
    [updateUrl]
  );

  const configured = balance?.configured === true;
  const yearBalance = configured ? balance.year : null;
  const income = sumOf(items, 'bedrag inkomst');
  const expense = sumOf(items, 'bedrag uitgaven');
  const incomeCount = items.filter((item) => Number(item['bedrag inkomst'])).length;
  const expenseCount = items.filter((item) => Number(item['bedrag uitgaven'])).length;
  const isRunningYear = String(new Date().getFullYear()) === String(year);
  const sincePoints = yearBalance?.points || [];

  const openRow = (id) => {
    setModalOpen(true);
    setSelectedId(id);
  };

  const rows = items.map((item) => [
    item.Kasboeknummer,
    <div style={{ margin: '-4px 0' }}>
      <Button size="micro" onClick={() => openRow(item.boekingsnummer)} icon={MaximizeIcon} accessibilityLabel={`Open entry ${item.Kasboeknummer}`} />
    </div>,
    formatDate(item.datum),
    item.omschrijving,
    item['bedrag inkomst'] ? formatToCurrency(item['bedrag inkomst']) : '',
    item['bedrag uitgaven'] ? formatToCurrency(item['bedrag uitgaven']) : '',
    item.Saldo == null ? <Text tone="subdued">—</Text> : <Text fontWeight="semibold">{formatToCurrency(item.Saldo)}</Text>
  ]);

  // the balance the year starts from, as the last (oldest) line of the list
  if (sincePoints.length) {
    rows.push([
      '',
      '',
      sincePoints[0].date,
      <Text tone="subdued">{sincePoints[0].date === `${year}-01-01` ? 'Opening balance' : 'Counted opening balance'}</Text>,
      '',
      '',
      <Text tone="subdued">{formatToCurrency(sincePoints[0].balance)}</Text>
    ]);
  }

  const lastBalance = sincePoints.length ? sincePoints[sincePoints.length - 1].balance : yearBalance?.closing;
  const totals = items.length > 0 ? ['', '', '', '', formatToCurrency(income), formatToCurrency(expense), lastBalance == null ? '' : formatToCurrency(lastBalance)] : null;

  const openingTile = () => {
    if (!configured) return { value: '—', detail: 'Running balance not set up' };
    if (yearBalance.opening != null) {
      return { value: formatToCurrency(yearBalance.opening), detail: yearBalance.closing != null ? `Closing ${formatToCurrency(yearBalance.closing)}` : `On 1 January ${year}` };
    }
    if (yearBalance.closing != null) return { value: '—', detail: `Closing ${formatToCurrency(yearBalance.closing)} (counted)` };
    return { value: '—', detail: `Balances start on ${formatWallClockDate(balance.opening.date)}` };
  };

  return (
    <Page
      title="Cashbook"
      fullWidth
      primaryAction={{
        content: 'New cashbook entry',
        onAction: () => openRow('new')
      }}
      secondaryActions={
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <div style={{ minWidth: 130 }}>
            <Select label="Year" labelInline options={generateArrayOfYears()} onChange={handleYearChange} value={parseInt(year) || ''} />
          </div>
          <Button icon={ExportIcon} onClick={exportExcel} loading={exporting} accessibilityLabel={`Export ${year} to Excel`}>
            Export
          </Button>
        </InlineStack>
      }
    >
      <Toasts toast={toast} setToast={setToast} errorToast={errorToast} setErrorToast={setErrorToast} toastContent={'Transaction saved'} />
      <CashbookModal modalOpen={modalOpen} setModalOpen={setModalOpen} id={selectedId} setId={setSelectedId} setToast={setToast} setErrorToast={setErrorToast} fetchTransactions={fetchTransactions} />
      <BlockStack gap="400">
        {balance && !configured ? (
          <Banner tone="warning" title="The running balance is not set up yet">
            <p>Run server/sql/add_boekingen_saldo.sql once on this database to store the opening balance and fill the balance column.</p>
          </Banner>
        ) : null}
        <InlineGrid columns={{ xs: 2, lg: 4 }} gap="400">
          <KpiTile
            label="Cash in hand"
            value={configured ? formatToCurrency(balance.current.balance) : '—'}
            detail={configured ? (balance.current.entryId ? `After the last entry on ${formatWallClockDate(balance.current.date)}` : 'No entries since the opening balance') : null}
            spark={isRunningYear && sincePoints.length > 2 ? sincePoints.map((point) => point.balance) : null}
            accent={COLORS.cash}
            loading={loading}
          />
          <KpiTile label={`Opening balance ${year}`} {...openingTile()} accent={COLORS.previous} loading={loading} />
          <KpiTile label={`Income ${year}`} value={formatToCurrency(income)} detail={entriesLabel(incomeCount)} accent={COLORS.revenue} loading={loading} />
          <KpiTile
            label={`Expenses ${year}`}
            value={formatToCurrency(expense)}
            detail={`${entriesLabel(expenseCount)} · net ${formatToCurrency(income - expense)}`}
            accent={COLORS.costs}
            loading={loading}
          />
        </InlineGrid>
        <Card padding="0">
          <Tabs tabs={TABS} selected={tab} onSelect={handleTabChange} />
          {tab === 0 ? (
            <DataTable
              verticalAlign="middle"
              stickyHeader={true}
              increasedTableDensity={true}
              columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric']}
              headings={[<b>{'Code'}</b>, <b>{'Edit'}</b>, <b>{'Date'}</b>, <b>{'Description'}</b>, <b>{'Income'}</b>, <b>{'Expense'}</b>, <b>{'Balance'}</b>]}
              showTotalsInFooter
              rows={rows}
              totals={totals}
              footerContent={`${entriesLabel(items.length)} in ${year}`}
            />
          ) : (
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingSm">
                  Cash balance {year}
                </Text>
                {!configured ? (
                  <Text tone="subdued">The running balance is not set up yet.</Text>
                ) : sincePoints.length ? (
                  <>
                    <CashBalanceChart points={sincePoints} />
                    <CashBalanceChartCaption opening={balance.opening} />
                  </>
                ) : (
                  <Text tone="subdued">
                    {yearBalance.closing != null
                      ? `No balance path for ${year}: the running balance starts from the counted ${formatToCurrency(balance.opening.amount)} on ${formatWallClockDate(
                          balance.opening.date
                        )}, which is the closing balance of ${year}.`
                      : `No balance for ${year}: the running balance starts on ${formatWallClockDate(balance.opening.date)}.`}
                  </Text>
                )}
              </BlockStack>
            </Box>
          )}
        </Card>
      </BlockStack>
    </Page>
  );
}
